import { useEffect, useMemo, useState } from 'react';
import HostApi from '../../../api/host/HostApi';
import HostTarget from '../../../types/host/HostTarget';
import HostStatisticV2 from '../../../types/host/HostStatisticV2';
import { EChartPoint } from '../../chart/echarts/types';
import { useTimeRange } from '../../../context/TimeRangeContext';
import { buildApiQuery, getConvertedTimeToUTC } from '../../../helpers/timeRangeUtils';
import { getErrorMessage, showError } from '../../../utils/showAlert';
import { CHART_COLOURS_STATEMENTS, DATA_FAILED, DATA_LOADED, DATA_LOADING } from '../../Constants';
import { EChartTimeSeries } from '../../chart/echarts/types';
import Helper from '../../../helpers/Helper';

interface HostStatisticResult {
    loading: number;
    rows: HostStatisticV2[];
    timeSeries: EChartPoint[];
    peak: number | null;
}

export const toSeriesPoints = (rows: HostStatisticV2[], metric: string): EChartPoint[] => {
    return rows.map(row => [getConvertedTimeToUTC(row), Number(row[metric]) || 0]);
};

export const toMetricSeries = (rows: HostStatisticV2[], metrics: string[]): EChartTimeSeries[] => {
    return metrics.map((metric, index) => ({
        name: metric,
        chartType: 'bar',
        color: CHART_COLOURS_STATEMENTS[index],
        data: toSeriesPoints(rows, metric),
    }));
};

export const useHostStatistic = (host: HostTarget, metric: string): HostStatisticResult => {
    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const [rows, setRows] = useState<HostStatisticV2[]>([]);
    const [timeSeries, setTimeSeries] = useState<EChartPoint[]>([]);
    const { timeRange } = useTimeRange();

    useEffect(() => {
        let cancelled = false;
        setLoading(DATA_LOADING);

        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;

        HostApi.getHostStatisticV2Time(buildApiQuery(timeRange), [host.id], limit)
            .then(data => {
                if (cancelled) {
                    return;
                }
                setRows(data);
                setTimeSeries(toSeriesPoints(data, metric));
                setLoading(DATA_LOADED);
            })
            .catch(error => {
                if (cancelled) {
                    return;
                }
                showError('Failed to retrieve host statistic time', getErrorMessage(error));
                setLoading(DATA_FAILED);
            });

        return () => {
            cancelled = true;
        };
    }, [timeRange.from.valueOf(), timeRange.to.valueOf(), timeRange.interval, host, metric]);

    const peak = useMemo(() => Helper.getPeakValue(timeSeries.map(point => point[1])), [timeSeries]);

    return { loading, rows, timeSeries, peak };
};
